import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import "./BlogPostPage.css";
import "./BlogArticle.css";

/* /widgets: free embeddable widgets for bloggers and publishers (division A6
   of the growth plan). The embed snippet carries a visible credit link OUTSIDE
   the iframe, on the host page, which is what actually builds the backlink;
   links inside an iframe do not count for the embedding page. The widget files
   are static (public/widgets/*.html), noindex, with no external requests, so
   embedding them costs a publisher nothing. muhurat.html runs no JS; mulank.html
   and bhagyank.html each run one small inline script inside their own iframe. */

const URL = "https://astroaura.ai/widgets";

const EMBED_SNIPPET = `<iframe src="https://astroaura.ai/widgets/muhurat.html"
  title="Shubh Vivah Muhurat 2026 by Aura AI"
  width="100%" height="430" style="max-width:440px;border:0;border-radius:16px"
  loading="lazy"></iframe>
<p style="font-size:13px;margin-top:6px">
  <a href="https://astroaura.ai/muhurat">Shubh muhurat data by Aura AI</a>
</p>`;

const MULANK_SNIPPET = `<iframe src="https://astroaura.ai/widgets/mulank.html"
  title="Mulank calculator by Aura AI"
  width="100%" height="440" style="max-width:440px;border:0;border-radius:16px"
  loading="lazy"></iframe>
<p style="font-size:13px;margin-top:6px">
  <a href="https://astroaura.ai/blog/mulank-kaise-nikale">Mulank calculator by Aura AI</a>
</p>`;

const BHAGYANK_SNIPPET = `<iframe src="https://astroaura.ai/widgets/bhagyank.html"
  title="Bhagyank calculator by Aura AI"
  width="100%" height="490" style="max-width:440px;border:0;border-radius:16px"
  loading="lazy"></iframe>
<p style="font-size:13px;margin-top:6px">
  <a href="https://astroaura.ai/blog/mulank-kaise-nikale">Bhagyank calculator by Aura AI</a>
</p>`;

const preStyle: React.CSSProperties = {
  background: "rgba(255,255,255,.06)",
  border: "1px solid rgba(255,255,255,.14)",
  borderRadius: 12,
  padding: "16px 18px",
  fontSize: ".78rem",
  lineHeight: 1.6,
  overflowX: "auto",
  whiteSpace: "pre-wrap",
};

const copyBtnStyle: React.CSSProperties = {
  background: "linear-gradient(96deg,#ed0687,#fe6c0f)",
  border: "none",
  color: "#fff",
  fontWeight: 700,
  borderRadius: 999,
  padding: "11px 22px",
  cursor: "pointer",
};

const WidgetsPage: React.FC = () => {
  const [copied, setCopied] = useState<string | null>(null);
  const copy = (key: string, snippet: string) => {
    try {
      navigator.clipboard.writeText(snippet).then(
        () => {
          setCopied(key);
          window.setTimeout(() => setCopied(null), 2000);
        },
        () => {}
      );
    } catch {
      /* clipboard unavailable */
    }
  };

  return (
    <div className="astro-aura-container">
      <Helmet>
        <title>Free Astrology Widgets for Your Website | Aura AI</title>
        <meta
          name="description"
          content="Embed free astrology widgets on your blog: Shubh Vivah Muhurat 2026 dates, plus mulank and bhagyank numerology calculators. One snippet, no tracking."
        />
        <link rel="canonical" href={URL} />
      </Helmet>

      <main className="blog-main">
        <nav className="breadcrumb" aria-label="Breadcrumb">
          <ol>
            <li>
              <Link to="/">Home</Link>
            </li>
            <li aria-current="page">Widgets</li>
          </ol>
        </nav>

        <section className="blog-header">
          <div className="blog-meta">
            <span>Free Widgets</span>
            <span className="dot">•</span>
            <span className="small-title">For bloggers &amp; publishers</span>
          </div>
          <h1 className="blog-title">
            Free Astrology Widgets for Your Website
          </h1>
          <p className="subtitle">
            Writing about the wedding season, muhurat dates, numerology or
            Vedic astrology? Embed our widgets free with one copy-paste snippet.
            No tracking, no external requests, nothing added to your page
            beyond a lazy-loaded iframe.
          </p>
        </section>

        <section className="blog-content">
          <h2>Shubh Vivah Muhurat 2026 widget</h2>
          <p>
            The verified November-December 2026 wedding dates with their
            nakshatras, the same data behind our{" "}
            <Link to="/blog/shadi-muhurat-nov-dec-2026">
              shadi muhurat guide
            </Link>
            . It renders like this:
          </p>

          <iframe
            src="/widgets/muhurat.html"
            title="Shubh Vivah Muhurat 2026 by Aura AI"
            width="100%"
            height="430"
            style={{ maxWidth: 440, border: 0, borderRadius: 16 }}
            loading="lazy"
          />

          <h2>How to embed it</h2>
          <p>
            Copy the snippet below into your article or template. Please keep
            the credit line under the widget, it is the only thing we ask for
            in return.
          </p>
          <pre style={preStyle}>{EMBED_SNIPPET}</pre>
          <p>
            <button
              type="button"
              onClick={() => copy("muhurat", EMBED_SNIPPET)}
              style={copyBtnStyle}
            >
              {copied === "muhurat" ? "Copied" : "Copy embed code"}
            </button>
          </p>

          <h2>Mulank numerology calculator widget</h2>
          <p>
            Your readers enter a date of birth and get their mulank (birth
            number, 1 to 9), its ruling graha and a short classical meaning.
            Mulank is the birth day reduced to a single digit, so someone born
            on the 29th is 2 + 9 = 11, then 1 + 1 = 2. It follows the
            reader's light or dark mode and fits columns down to 300px wide.
          </p>

          <iframe
            src="/widgets/mulank.html"
            title="Mulank calculator by Aura AI"
            width="100%"
            height="440"
            style={{ maxWidth: 440, border: 0, borderRadius: 16 }}
            loading="lazy"
          />

          <h2>How to embed the mulank calculator</h2>
          <p>
            Same as above: paste the snippet and keep the credit line under the
            widget. The calculation runs inside the iframe, so nothing touches
            your page's scripts or styles.
          </p>
          <pre style={preStyle}>{MULANK_SNIPPET}</pre>
          <p>
            <button
              type="button"
              onClick={() => copy("mulank", MULANK_SNIPPET)}
              style={copyBtnStyle}
            >
              {copied === "mulank" ? "Copied" : "Copy embed code"}
            </button>
          </p>

          <h2>Bhagyank (life path number) calculator widget</h2>
          <p>
            Bhagyank, the destiny or life path number, uses the full date of
            birth: every digit of the day, month and year is added and reduced
            to a single digit. For 14 July 1990 that is 1 + 4 + 0 + 7 + 1 + 9 +
            9 + 0 = 31, then 3 + 1 = 4. Readers get their bhagyank (1 to 9), its
            ruling graha, a short classical meaning and the working shown, in
            light or dark mode, down to 300px wide.
          </p>

          <iframe
            src="/widgets/bhagyank.html"
            title="Bhagyank calculator by Aura AI"
            width="100%"
            height="490"
            style={{ maxWidth: 440, border: 0, borderRadius: 16 }}
            loading="lazy"
          />

          <h2>How to embed the bhagyank calculator</h2>
          <p>
            Paste the snippet and keep the credit line under the widget, which
            links to our guide on finding mulank and bhagyank.
          </p>
          <pre style={preStyle}>{BHAGYANK_SNIPPET}</pre>
          <p>
            <button
              type="button"
              onClick={() => copy("bhagyank", BHAGYANK_SNIPPET)}
              style={copyBtnStyle}
            >
              {copied === "bhagyank" ? "Copied" : "Copy embed code"}
            </button>
          </p>

          <h2>Terms, in plain words</h2>
          <ul>
            <li>Free for any website, commercial or personal.</li>
            <li>
              Keep the visible credit link ("Shubh muhurat data by Aura AI",
              "Mulank calculator by Aura AI" or "Bhagyank calculator by Aura
              AI") with the widget.
            </li>
            <li>
              Do not alter the data inside the widget; it is verified against
              published panchang sources and refreshed every season.
            </li>
            <li>
              Want a different widget (daily panchang, kundli matching score,
              festival countdown)? Email{" "}
              <a href="mailto:contact@astroaura.ai">contact@astroaura.ai</a>{" "}
              and we will build it.
            </li>
          </ul>

          <h2>Why we made this</h2>
          <p>
            Wedding-season articles quote muhurat dates anyway; most copy them
            from somewhere unverified. The muhurat widget gives your readers the
            cross-checked dates with nakshatras, styled, responsive and always
            current. Numerology posts get an interactive calculator instead of
            a static table. Either way your page stays fast, because the widgets
            load lazily inside their own iframe.
          </p>
        </section>
      </main>
    </div>
  );
};

export default WidgetsPage;
